import { useEffect, useState } from "react";
import {
  ConfirmDialog,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import { toClientApiError } from "../../api/httpClient";
import {
  settingsPermissionsApi,
  type MembershipPermissionRow,
  type MembershipPermissions,
} from "../../api/settingsPermissionsApi";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { UiStateMessage } from "../../engines";
import type { UiLoadState } from "../../engines";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { resolvePermissionLabel } from "./permissionLabels";

function effectLabel(value: "grant" | "deny" | null): string {
  if (value === "grant") {
    return "מענק";
  }
  if (value === "deny") {
    return "דחייה";
  }
  return "—";
}

export type MembershipPermissionsPanelProps = {
  membershipId: string | null;
  active: boolean;
};

/** Inline panel for membership permission overrides (role vs effective). */
export function MembershipPermissionsPanel({
  membershipId,
  active,
}: MembershipPermissionsPanelProps) {
  const entityLabels = useEntityLabels();
  const [item, setItem] = useState<MembershipPermissions | null>(null);
  const [state, setState] = useState<UiLoadState>("loading");
  const [error, setError] = useState<string | undefined>();
  const [selected, setSelected] = useState<MembershipPermissionRow | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideEffect, setOverrideEffect] = useState<"grant" | "deny">("grant");
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = async () => {
    if (!active || !membershipId) {
      return;
    }
    setState("loading");
    setError(undefined);
    try {
      const result = await settingsPermissionsApi.getMembershipPermissions(membershipId);
      setItem(result);
      setState("ready");
      setSelected((current) =>
        current
          ? (result.permissions.find((row) => row.key === current.key) ?? null)
          : null,
      );
    } catch (err) {
      const apiError = toClientApiError(err);
      if (apiError.status === 403) {
        setState("permission");
      } else if (apiError.status === 404) {
        setError("המשתמש לא נמצא בארגון זה");
        setState("error");
      } else {
        setError(apiError.message);
        setState("error");
      }
    }
  };

  useEffect(() => {
    if (!active) {
      setSelected(null);
      setActionError(null);
      setOverrideOpen(false);
      setClearOpen(false);
      setItem(null);
      return;
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when tab becomes active / membership changes
  }, [active, membershipId]);

  const selectRow = (row: Record<string, unknown>) => {
    const key = String(row.key ?? "");
    const found = item?.permissions.find((entry) => entry.key === key) ?? null;
    setSelected(found);
    setActionError(null);
    return true;
  };

  const handleSetOverride = async () => {
    if (!item || !selected) {
      return;
    }
    setOverrideBusy(true);
    setActionError(null);
    try {
      await settingsPermissionsApi.setOverride(
        item.membershipId,
        selected.key,
        overrideEffect,
      );
      setOverrideOpen(false);
      await load();
    } catch (err) {
      setActionError(toClientApiError(err).message);
      setOverrideOpen(false);
    } finally {
      setOverrideBusy(false);
    }
  };

  const handleClearOverride = async () => {
    if (!item || !selected) {
      return;
    }
    setClearBusy(true);
    setActionError(null);
    try {
      await settingsPermissionsApi.clearOverride(item.membershipId, selected.key);
      setClearOpen(false);
      await load();
    } catch (err) {
      setActionError(toClientApiError(err).message);
      setClearOpen(false);
    } finally {
      setClearBusy(false);
    }
  };

  const selectedLabel = selected
    ? resolvePermissionLabel(selected.labelHe, entityLabels)
    : "";

  const columns = [
    {
      key: "labelHeResolved",
      title: "הרשאה",
      sortable: true,
      filterable: true,
      minWidth: 200,
    },
    {
      key: "moduleLabelHeResolved",
      title: "מודול",
      sortable: true,
      filterable: true,
      minWidth: 120,
    },
    {
      key: "key",
      title: "מפתח",
      sortable: true,
      filterable: true,
      minWidth: 160,
    },
    {
      key: "roleEffectLabel",
      title: "מהתפקיד",
      filterable: true,
      minWidth: 90,
    },
    {
      key: "overrideEffectLabel",
      title: "חריגה",
      filterable: true,
      minWidth: 90,
    },
    {
      key: "effectiveLabel",
      title: "אפקטיבי",
      filterable: true,
      minWidth: 90,
    },
    {
      key: "actions",
      title: "פעולות",
      minWidth: 160,
      render: (_value: unknown, row: Record<string, unknown>) => {
        const key = String(row.key ?? "");
        const found = item?.permissions.find((entry) => entry.key === key) ?? null;
        if (!found) {
          return null;
        }
        return (
          <TableRowActions>
            <QuietButton
              title="הגדרת חריגה"
              tone="accent"
              onClick={() => {
                setSelected(found);
                setOverrideEffect(found.overrideEffect ?? "grant");
                setOverrideOpen(true);
              }}
            >
              חריגה
            </QuietButton>
            {found.overrideEffect ? (
              <QuietButton
                title="ניקוי חריגה"
                onClick={() => {
                  setSelected(found);
                  setClearOpen(true);
                }}
              >
                ניקוי
              </QuietButton>
            ) : null}
          </TableRowActions>
        );
      },
    },
  ];

  const tableRows =
    item?.permissions.map((row) => ({
      ...row,
      id: row.key,
      labelHeResolved: resolvePermissionLabel(row.labelHe, entityLabels),
      moduleLabelHeResolved: resolvePermissionLabel(row.moduleLabelHe, entityLabels),
      roleEffectLabel: effectLabel(row.roleEffect),
      overrideEffectLabel: effectLabel(row.overrideEffect),
      effectiveLabel: row.effective.allowed ? "מותר" : "חסום",
    })) ?? [];

  if (!active || !membershipId) {
    return null;
  }

  if (state === "loading" || state === "error" || state === "permission") {
    return (
      <div className="related-panel">
        <UiStateMessage state={state} errorMessage={error} />
      </div>
    );
  }

  if (!item) {
    return null;
  }

  const roleBanner =
    item.roleNames?.length > 0
      ? item.roleNames.join(", ")
      : item.roleKeys.length > 0
        ? item.roleKeys.join(", ")
        : "אין";

  return (
    <div className="related-panel">
      <FormAlert severity="info">
        תפקידים: {roleBanner} — כאן אפשר להוסיף חריגה פר־הרשאה מעבר לתבנית התפקיד.
      </FormAlert>

      {actionError ? <FormAlert severity="error">{actionError}</FormAlert> : null}
      {selected ? (
        <FormAlert severity="info">
          נבחר: {selectedLabel}
        </FormAlert>
      ) : null}

      <TableKit
        columns={columns}
        data={tableRows as unknown as Array<Record<string, unknown>>}
        loading={false}
        clientSideMode
        emptyMessage="אין הרשאות בקטלוג."
        exportFileName="membership-permissions"
        showExport
        showFilters
        showColumnPicker
        hiddenColumnKeys={["key"]}
        onRowClick={selectRow}
        onRowDoubleClick={selectRow}
        onOpenCard={selectRow}
      />

      <FormDialog
        open={overrideOpen}
        onClose={() => {
          if (!overrideBusy) {
            setOverrideOpen(false);
          }
        }}
        size="sm"
      >
        <FormCard
          title="הגדרת חריגה"
          onSave={() => {
            void handleSetOverride();
          }}
          onCancel={() => setOverrideOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={overrideBusy}
        >
          <FormSection>
            <FormGrid cols={1}>
              <FormField
                label="הרשאה"
                value={selectedLabel}
                onChange={() => undefined}
                disabled
              />
              <FormField
                label="אפקט"
                type="select"
                value={overrideEffect}
                onChange={(value) =>
                  setOverrideEffect(value === "deny" ? "deny" : "grant")
                }
                disabled={overrideBusy}
                options={[
                  { value: "grant", label: "מענק" },
                  { value: "deny", label: "דחייה" },
                ]}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        title="ניקוי חריגה"
        message={
          selected
            ? `לנקות את החריגה עבור "${selectedLabel}" ולחזור להרשאות התפקיד?`
            : ""
        }
        confirmText="ניקוי"
        cancelText="ביטול"
        loading={clearBusy}
        onConfirm={() => {
          void handleClearOverride();
        }}
        onCancel={() => setClearOpen(false)}
      />
    </div>
  );
}

/** @deprecated Prefer MembershipPermissionsPanel inside UserCard tabs. */
export type MembershipPermissionsDialogProps = {
  open: boolean;
  membershipId: string | null;
  onClose: () => void;
};

export function MembershipPermissionsDialog({
  open,
  membershipId,
  onClose,
}: MembershipPermissionsDialogProps) {
  return (
    <FormDialog open={open} onClose={onClose} size="lg">
      <FormCard title="הרשאות משתמש" onCancel={onClose} cancelLabel="סגירה">
        <MembershipPermissionsPanel membershipId={membershipId} active={open} />
      </FormCard>
    </FormDialog>
  );
}
